import { Fragment, useEffect, useState } from "react";
import { t } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserPlus,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/pro-regular-svg-icons";

import { BrandedWhitSupporetdText } from "../atoms/BrandedWithSupportedText";
import { InputWithCornerHint } from "../atoms/InputWithCornerHint";
import ColumnText from "../molecules/ColumnText";
import { Empty } from "./Empty";

import { Fee } from "@/app/fees/fees";

import {
  useFeesUserQuery,
} from "@/app/fees-user/fees-user.api";

type option = {
  id: string | number;
  title: string;
  description: string;
  function: () => void;
};

export type TableListCuotasUserProps = {
  coutas: Fee[];
  titleBranded: string;
  titleBrandedActiveFee: string;
  OptionsBrandedActiveFee: option[];
  activeCurrentQuota: any;
  onClickAssingFee: (id: number) => void;
  onClickModifyFee: (fee: Fee) => void;
  onClickNextPayment: (fee: Fee) => void;
  onClickPay: (id: number) => void;
  onClickDiscontinue: (fee: Fee) => void;
  onClickCancelFee: (fee: Fee) => void;
  onClickPauseFee: (fee: Fee) => void;
  user: any;
};

const TableListCuotasUser: React.FC<TableListCuotasUserProps> = ({
  coutas,
  titleBranded,
  titleBrandedActiveFee,
  OptionsBrandedActiveFee,
  activeCurrentQuota,
  onClickAssingFee,
  onClickModifyFee,
  onClickNextPayment,
  onClickPay,
  onClickDiscontinue,
  onClickCancelFee,
  onClickPauseFee,
  user,
}) => {
  const [filteredFees, setFilteredCuotas] = useState([]);

  const feesUserQuery = useFeesUserQuery({ branchId: user.branchId, userId: user.id });

  const [feesUser, setFeesUser] = useState<any>(null);

  useEffect(() => {
    if (feesUserQuery.isSuccess) {
      setFeesUser(feesUserQuery.data);
    }
  }, [feesUserQuery]);

  useEffect(() => {
    setFilteredCuotas(coutas);
  }, [coutas])

  const onSearch = (value: string) => {
    const filtered = coutas.filter((curso) =>
      curso.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredCuotas(filtered);
  };

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const getPeriodText = (
    intervalCount: number,
    interval: string,
    trialPeriodDays: number | null
  ) => {
    let periodText = "";

    if (intervalCount === 1 && interval === "month") {
      periodText = t("stripe.views.month");
    } else if (intervalCount === 3 && interval === "month") {
      periodText = t("stripe.views.quarterly");
    } else if (intervalCount === 2 && interval === "month") {
      periodText = t("stripe.views.bimonthly");
    } else if (intervalCount === 6 && interval === "month") {
      periodText = t("stripe.views.biannual");
    } else if (intervalCount === 1 && interval === "year") {
      periodText = t("stripe.views.year");
    }

    if (intervalCount !== 0) {
      periodText += " + ";
    }

    periodText += `${trialPeriodDays ?? 0} días`;

    return periodText;
  };

  const getPrice = (fee, statusUser, status) => {
    const priceInit = statusUser
      ? fee.price - fee.price * (statusUser.discount / 100)
      : fee.price;
  
    return status
      ? priceInit - priceInit * (fee.discount / 100)
      : priceInit;
  };
  
  const getPriceProportional = (fee, statusUser, status) => {
    const priceProportionalInit = statusUser
      ? fee.price_proportional - fee.price_proportional * (statusUser.discount / 100)
      : fee.price_proportional;
  
    return status
      ? priceProportionalInit - priceProportionalInit * (fee.discount / 100)
      : priceProportionalInit;
  };
  
  const getStatusUserDiscount = (user, feeId) => {
    // Simula la lógica de `getStatusUserDiscount` en PHP
    return user.discounts?.find((discount) => discount.feeId === feeId) || null;
  };
  
  const getStatusFeeUserDiscount = (fee, userId) => {
    // Simula la lógica de `getStatusFeeUserDiscount` en PHP
    return fee.userDiscounts?.find((discount) => discount.userId === userId) || null;
  };

  const formatDate = (date: any) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    };
    return new Date(date)
      .toLocaleDateString("en-GB", options)
      .replace(/\//g, "-");
  };

  const FeeStatusCell = ({ fee, user, activeCurrentQuota }) => {
    const statusUser = getStatusUserDiscount(user, fee.id);
    const status = getStatusFeeUserDiscount(fee, user.id);
  
    const price = getPrice(fee, statusUser, status);
    const priceProportional = getPriceProportional(fee, statusUser, status);
  
    const feeUser = feesUser?.find(
      (feeUser) => feeUser.feeId === fee.id && feeUser.userId === user.id
    );
    const feeUserStatus = feeUser ? feeUser.status : null;

    return (
      <td>
        {activeCurrentQuota && (
          <div data-fee-info-active={fee.id}>
            {fee.id === activeCurrentQuota.feeId && feeUser && (
              <>
                {feeUser.status === "active" && (
                  <div className="flex items-center">
                    <span className="label label-success label-dot mr-2"></span>
                    <div>
                      <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium bg-green-50 text-green-700 ring-green-600/20">
                        {t("fees.views.state_payment_paid")}
                      </span>
                      <br />
                      <span
                        className="flex justify-start text-gray-500"
                        style={{ fontSize: "12px" }}
                      >
                        {formatDate(feeUser.dateFinish)}
                      </span>

                      {feeUser.daysPause > 0 ? (
                        <>
                          <span
                            className="inline-flex items-center rounded-md px-2 py-1 font-medium bg-gray-50 text-gray-700 ring-gray-600/20"
                            style={{ fontSize: "10px" }}
                          >
                            {t("fees.views.state_paused_until")}:{" "}
                            {formatDate(feeUser.datePause)}
                          </span>
                        </>
                      ): null}

                      {activeCurrentQuota.remaining ? (
                        <span
                            className="inline-flex items-center rounded-md px-2 py-1 font-medium bg-orange-50 text-orange-700 ring-orange-600/20"
                            style={{ fontSize: "10px" }}
                          >
                            {t("fees.views.remaining_amount", {
                              amount: activeCurrentQuota.remainingAmount,
                              currency: fee.currency,
                            })}
                        </span>
                      ): null}
                    </div>
                  </div>
                )}
                {feeUser.status === "unsubscribe" &&
                  new Date(feeUser.date_finish) >= new Date() && (
                    <div className="flex items-center">
                      <span className="label label-warning label-dot mr-2"></span>
                      <div>
                        <span className="text-warning">
                          {t("fees.views.state_payment_low")}
                        </span>
                        <br />
                        <span
                          className="flex justify-start text-gray-500"
                          style={{ fontSize: "12px" }}
                        >
                          {formatDate(feeUser.dateFinish)}
                        </span>
                      </div>
                    </div>
                  )}
                {feeUser.status === "delayed" && (
                  <div className="flex items-center">
                    <span className="label label-danger label-dot mr-2"></span>
                    <div>
                      <span className="text-danger">
                        {t("fees.views.state_payment_delay")}
                      </span>
                      <br />
                      <span
                        className="flex justify-start text-gray-500"
                        style={{ fontSize: "12px" }}
                      >
                        {formatDate(feeUser.dateFinish)}
                      </span>
                      {feeUser.status_aux === "Payment declined" && (
                        <>
                          <br />
                          <span>{t("fees.views.rejected_payment")}</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
  
        <div data-fee-info={fee.id}>
          {user.feeNext && fee.id === user.feeNext.feeId && (
            <div className="flex items-center">
              <span className="label label-success label-dot mr-2"></span>
              <div>
                <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium bg-green-50 text-green-700 ring-green-600/20">
                  {t("fees.views.state_payment_next")}
                </span>
                <br />
                <span
                  className="flex justify-start text-gray-500"
                  style={{ fontSize: "12px" }}
                >
                  {formatDate(activeCurrentQuota.dateFinish)}
                </span>
              </div>
            </div>
          )}
        </div>
      </td>
    );
  };

  const renderActionButton = (fee) => {
    const options = [];

    const viewAction = user.stateFee ? user.stateFee.main : true;
    var active_current_quota = user.stateFee;

    const viewActionAll = active_current_quota
      ? active_current_quota.status !== "unsubscribe"
      : true;

    if (
      fee.active &&
      viewActionAll &&
      !user.feePaymentPending &&
      !user.paymentPendingRecurrent
    ) {
      if (viewAction) {
        if (active_current_quota) {
          if (fee.id === active_current_quota.feeId && !user.feeNext) {
            if (active_current_quota.status !== "unsubscribe") {

              options.push({
                title: t("share.views.edit"),
                description: 'Cambiar la fecha de renovación próxima.',
                onClick: () => onClickModifyFee(fee),
                primary: true,
                current: true,
              });

              if (!active_current_quota.remaining) {
                options.push({
                  title: t("fees.views.next_pay"),
                  description: 'Cambiar precio del siguiente cobro; no cobra la siguiente cuota.',
                  onClick: () => onClickNextPayment(fee),
                  primary: true,
                  current: false,
                });
              }

              if (!user.paymentPendingRecurrent) {
                options.push({
                  title: t("fees.views.button_pay"),
                  description: 'Realizar el pago pendiente inmediatamente.',
                  onClick: () => onClickPay(fee.id),
                  primary: false,
                  current: false,
                });
              }

              if (active_current_quota.daysPause === 0) {
                options.push({
                  title: t("fees.views.button_low"),
                  description: 'Cancelar la cuota activa; para cambiar de cuota simplemente asignale otra cuota sin darle de baja.',
                  onClick: () => onClickDiscontinue(fee),
                  primary: true,
                  current: false,
                });
              }

              // {user.getFeeHistory(fee.id) && ( )}

              options.push({
                title: t("fees.views.cancel"),
                description: t("fees.views.cancel"),
                onClick: () => onClickCancelFee(fee),
                primary: false,
                current: false,
              });

              if (active_current_quota.daysPause === 0 &&
                active_current_quota.method !== "card" &&
                active_current_quota.method !== "direct_debit" &&
                !active_current_quota.remaining) {
                options.push({
                  title: t("fees.views.pause"),
                  description: 'Pausa o reanuda las renovaciones automaticas con cobros recurrentes.',
                  onClick: () => onClickPauseFee(fee),
                  current: false,
                });
              }

              if (active_current_quota.daysPause > 0) {
                options.push({
                  title: 'Modificar pausa',
                  description: 'Modificar pausa',
                  onClick: () => onClickAssingFee(fee.id),
                  primary: true,
                  current: false,
                });
              }

              if (fee.people > 1 &&
                active_current_quota.payment &&
                !active_current_quota.remaining) {
                options.push({
                  title: <FontAwesomeIcon
                              icon={faUserPlus}
                              aria-hidden="true"
                              className="h-5 w-5"
                            />,
                  description: 'Nuevo usuario',
                  onClick: () => onClickAssingFee(fee.id),
                  primary: true,
                  current: false,
                });
              }

              if ((active_current_quota.method === "card" ||
                active_current_quota.method === "direct_debit")) {
                options.push({
                  title: active_current_quota.recurrence
                              ? t("user.views.desactive_recurrency")
                              : "Activar recurrencia",
                  description: active_current_quota.recurrence
                              ? t("user.views.desactive_recurrency")
                              : "Activar recurrencia",
                  onClick: () => onClickAssingFee(fee.id),
                  current: false,
                });
              }

              return (
                <td>
                  <BrandedWhitSupporetdText
                    title={t("fees.views.button_action")}
                    options={options}
                  />
                </td>
              );
            } else {
              return (
                <button
                  className="text-white text-sm font-semibold inline-flex items-center rounded-l-md rounded-r-md bg-primary-600 p-2 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 focus:ring-offset-gray-50"
                  onClick={() => onClickAssingFee(fee.id)}
                >
                  {t("bond.views.assign")}
                </button>
              );
            }
          } else if (fee.id === active_current_quota.feeId && fee.people > 1) {
            return (
              <button
                className="text-white text-sm font-semibold inline-flex items-center rounded-l-md rounded-r-md bg-primary-600 p-2 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 focus:ring-offset-gray-50"
                onClick={() => onClickAssingFee(fee.id)}
              >
                <FontAwesomeIcon
                  icon={faUserPlus}
                  aria-hidden="true"
                  className="h-5 w-5"
                />
              </button>
            );
          } else if (
            fee.id === active_current_quota.feeId &&
            active_current_quota.days_pause === 0
          ) {
            if (
              active_current_quota.method !== "card" &&
              active_current_quota.method !== "direct_debit"
            ) {
              return (
                <button
                  className="btn btn-fmw-grey font-weight-bolder"
                  onClick={() => onClickAssingFee(fee.id)}
                >
                  {t("fees.views.pause")}
                </button>
              );
            }
          } else if (fee.id !== active_current_quota.feeId) {
            if (user.feeNext) {
              if (fee.id === user.feeNext.feeId) {
                return (
                  <span className="isolate inline-flex rounded-md shadow-sm">
                    <button
                      type="button"
                      className="relative inline-flex items-center rounded-l-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                      onClick={() => onClickDiscontinue(fee)}
                    >
                      {t("fees.views.button_low")}
                    </button>
                    <button
                      type="button"
                      className="relative -ml-px inline-flex items-center rounded-r-md bg-secondary px-3 py-2 text-sm font-semibold text-white ring-1 ring-inset ring-secondary-300 hover:bg-secondary-200 focus:z-10"
                      onClick={() => onClickCancelFee(fee)}
                    >
                      {t("fees.views.cancel")}
                    </button>
                  </span>
                );
              } else {
                return (
                  <button
                    className="text-white text-sm font-semibold inline-flex items-center rounded-l-md rounded-r-md bg-primary-600 p-2 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 focus:ring-offset-gray-50"
                    onClick={() => onClickAssingFee(fee.id)}
                  >
                    {t("bond.views.assign")}
                  </button>
                );
              }
            } else {
              return (
                <button
                  className="text-white text-sm font-semibold inline-flex items-center rounded-l-md rounded-r-md bg-primary-600 p-2 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 focus:ring-offset-gray-50"
                  onClick={() => onClickAssingFee(fee.id)}
                >
                  {t("bond.views.assign")}
                </button>
              );
            }
          }
        } else {
          return (
            <button
              onClick={() => onClickAssingFee(fee.id)}
              className="text-white text-sm font-semibold inline-flex items-center rounded-l-md rounded-r-md bg-primary-600 p-2 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 focus:ring-offset-gray-50"
            >
              {t("bond.views.assign")}
            </button>
          );
        }
      }
    }
  
    if (
      user.payment_pending_recurrent &&
      user.payment_pending_recurrent.id_product === fee.id
    ) {
      return (
        <>
          <button
            className="text-white text-sm font-semibold inline-flex items-center rounded-l-md rounded-r-md bg-primary-600 p-2 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 focus:ring-offset-gray-50"
            onClick={() => onClickAssingFee(fee.id)}
          >
            {t("fees.views.button_force_payment")}
          </button>
          <button
            className="text-white text-sm font-semibold inline-flex items-center rounded-l-md rounded-r-md bg-primary-600 p-2 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 focus:ring-offset-gray-50"
            onClick={() => onClickDiscontinue(fee)}
          >
            {t("fees.views.button_low")}
          </button>
        </>
      );
    }
  
    if (user.stateFee) {
      if (
        !fee.active &&
        fee.id === user.stateFee.feeId &&
        user.stateFee.status !== "unsubscribe"
      ) {
        return (
          <div className="btn-group">
            <button
              className="btn btn-fmw btn-lg dropdown-toggle"
              type="button"
              data-toggle="dropdown"
              aria-expanded="false"
            >
              {t("fees.views.button_action")}
            </button>
            <div className="dropdown-menu bg-light">
              <button
                className="text-white text-sm font-semibold inline-flex items-center rounded-l-md rounded-r-md bg-primary-600 p-2 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 focus:ring-offset-gray-50"
                onClick={() => onClickDiscontinue(fee)}
              >
                {t("fees.views.button_low")}
              </button>
              {/*
              {user.getFeeHistory(fee.id) && (
                <button
                  className="btn btn-fmw-pink font-weight-bolder btn-block"
                  onClick={() => onClickAssingFee(fee.id)}
                >
                  {t("fees.views.cancel")}
                </button>
              )}
              */}
            </div>
          </div>
        );
      }
    }
  
    return null;
  };

  return (
    <Fragment>
      <div className="mt-5 overflow-visible">
        <div className="">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4">
            <div></div>
            <div className="mt-2 sm:mt-0">
              <InputWithCornerHint
                type="text"
                label=""
                placeholder={t("store.views.form.search_by_name")}
                onChange={(e) => onSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="flow-root px-2 sm:px-3 lg:px-6">
            <div className="overflow-x-auto -mx-4 -my-2 sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full px-3 py-2 align-middle">
                {coutas.length === 0 ? (
                  <Empty texto={t("customer-to-gym-subscription.views.no_fee")} />
                ) : (
                  <Fragment>
                    <div className="overflow-x-auto min-h-96">
                      <table className="min-w-full divide-y divide-gray-300 border-t border-gray-300">
                        <thead>
                          <tr>
                            <th
                              scope="col"
                              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/12"
                            >
                              {t("customer-to-gym-subscription.views.name")}
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-2/12"
                            >
                              {t("customer-to-gym-subscription.views.description")}
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                            >
                              {t("customer-to-gym-subscription.views.renewal_period")}
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                            >
                              {t("customer-to-gym-subscription.views.price")}
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                            >
                              {t("customer-to-gym-subscription.views.discount")}
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                            >
                              {t("customer-to-gym-subscription.views.registration")}
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                            >
                              {t("fees.views.state_quota")}
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                            >
                              {t("user.views.info_payment_cash")}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {filteredFees.length  === 0 ? (
                            <tr>
                              <td colSpan={8} className="text-center py-5">
                                {t("customer-to-gym-subscription.views.no_fee")}
                              </td>
                            </tr>
                          ):(
                            filteredFees.map((fee) => (
                              <tr key={fee.id}>
                                <td className="px-3 py-3.5 text-left text-sm text-gray-500 w-1/12">
                                  {fee.name}
                                </td>
                                <td className="px-3 py-3.5 text-left text-sm text-gray-500 w-2/12">
                                  {fee.description}
                                </td>
                                <ColumnText
                                  title={getPeriodText(
                                    Number(fee.intervalCount),
                                    fee.interval,
                                    fee.trialPeriodDays
                                  )}
                                />
                                <ColumnText
                                  title={`${fee.price/100} ${fee.currency.toLocaleUpperCase()}`}
                                />
                                <ColumnText title={`${fee.repeatDiscount} (${fee.discount} %)`} />
                                <ColumnText
                                  title={
                                    <>
                                      {fee.registrationPayment ? (
                                        <>
                                          {fee.registrationPayment.price}{" "}
                                          <span className="text-uppercase">
                                            {fee.registrationPayment.currency}
                                          </span>
                                        </>
                                      ) : null}
                                    </>
                                  }
                                />
                                <ColumnText
                                  title={
                                    <>
                                      {fee.active === 1
                                        ? t("fees.views.fee_active")
                                        : fee.active === 0
                                        ? t("stripe.views.inactive")
                                        : t("fees.views.fee_inactive")}
                                    </>
                                  }
                                />

                                <td style={{ width: "80px" }}>
                                  {FeeStatusCell({fee, user, activeCurrentQuota: activeCurrentQuota})}
                                </td>

                                <td className="whitespace-nowrap px-3">
                                  <div className="flex justify-center">
                                    {renderActionButton(fee)}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </Fragment>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default TableListCuotasUser;
